import { useState, useEffect, useMemo } from 'react';

type UsePopupPositioningParams = {
  x: number;
  y: number;
  width: number;
  actualScreenX: number;
  actualScreenY: number;
  screenWidth: number;
  errorMessage: string;
  pageNumber?: number;
  isMobile?: boolean;
};

export const usePopupPositioning = ({ width, actualScreenX, actualScreenY, screenWidth, errorMessage, pageNumber, isMobile }: UsePopupPositioningParams) => {
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  useEffect(() => {
    const baseBuffer = 130;
    const messageBuffer = errorMessage ? Math.min(errorMessage.length * 2, 50) : 0;
    const topBuffer = baseBuffer + messageBuffer;
    const isNearTop = actualScreenY < topBuffer;

    // Special case: on first page, for fields near the top, show popup below the field
    if (isNearTop && pageNumber === 1) {
      setPlacement('bottom');
    } else {
      setPlacement('top');
    }
  }, [actualScreenY, errorMessage, pageNumber]);

  const positioning = useMemo(() => {
    const isSmallScreen = screenWidth <= 768;
    const isMediumScreen = screenWidth > 768 && screenWidth <= 1024;
    const isFieldNarrow = width < 50;
    const viewportWidth = window.innerWidth;
    const isOnRightEdge = actualScreenX > viewportWidth * 0.9;
    const isOnLeftEdge = actualScreenX < viewportWidth * 0.1;
    const isOnLeftHalf = actualScreenX < viewportWidth * 0.5;

    let shouldShowPopupLeft = false;

    // For smaller screens, show popup on left if field is on left half
    if (!isMobile) {
      return {
        shouldShowPopupLeft,
        isSmallScreen,
        isMediumScreen,
        isFieldNarrow,
        isOnRightEdge,
        isOnLeftHalf
      };
    }

    if (isSmallScreen && isOnLeftHalf) {
      shouldShowPopupLeft = true;
    } else if (isMediumScreen && isOnLeftEdge && isFieldNarrow) {
      shouldShowPopupLeft = true;
    } else if (isOnLeftEdge) {
      shouldShowPopupLeft = true;
    }

    return {
      shouldShowPopupLeft,
      isSmallScreen,
      isMediumScreen,
      isFieldNarrow,
      isOnRightEdge,
      isOnLeftHalf
    };
  }, [actualScreenX, width, screenWidth]);

  const popupPosition: 'top left' | 'top right' | 'bottom left' | 'bottom right' = useMemo(() => {
    const { shouldShowPopupLeft } = positioning;

    if (shouldShowPopupLeft) {
      return placement === 'bottom' ? 'bottom left' : 'top left';
    } else {
      return placement === 'bottom' ? 'bottom right' : 'top right';
    }
  }, [placement, positioning]);

  return {
    placement,
    popupPosition,
    ...positioning
  };
};
